import { ErrorBoundary } from 'modules/error';
import { dataTestId } from 'modules/experiments/constants';
import {
  CopyAssistContextProvider,
  ModalContextProvider,
  TakeoverExperimentContextProvider,
  UpdateExperimentFormProvider,
  VariantConfigurationContextProvider,
  VariantTemplateValidationContextProvider,
  useModalContext,
  useTakeoverExperimentContext,
} from 'modules/experiments/contexts';
import {
  getTakeoverCampaignId,
  getTakeoverExperimentLoading,
  getTakeoverVariantList,
} from 'modules/experiments/ducks/selectors';
import { usePrivateExperimentTakeover } from 'modules/experiments/takeover/hooks';
import { useSelector } from 'react-redux';
import { Product, Team } from 'teams';
import { MODAL_VARIANTS } from 'ui/modal';
import { Spinner } from 'ui/spinner';
import { Takeover } from 'ui/workflows/takeover';

import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { PageContainer } from '../CommonStyled';
import { ExperimentTakeoverBody } from './ExperimentTakeoverBody';
import { ExperimentTakeoverFooter } from './ExperimentTakeoverFooter';
import { ExperimentTakeoverHeader } from './ExperimentTakeoverHeader';
import { Overlay } from './Styled';

type ExperimentEditDrawerProps = {
  onClose: () => void;
};

const ExperimentEditDrawerContext: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <ModalContextProvider>
    <TakeoverExperimentContextProvider>
      <CopyAssistContextProvider>
        <UpdateExperimentFormProvider>
          <VariantTemplateValidationContextProvider>
            <VariantConfigurationContextProvider>{children}</VariantConfigurationContextProvider>
          </VariantTemplateValidationContextProvider>
        </UpdateExperimentFormProvider>
      </CopyAssistContextProvider>
    </TakeoverExperimentContextProvider>
  </ModalContextProvider>
);

const ExperimentTakeOver = ({ onClose }: ExperimentEditDrawerProps) => {
  const { t } = useExperimentsTranslations();
  const { closeExperimentTakeover } = usePrivateExperimentTakeover();
  const {
    overlay: { isOverlayEnable },
    unsavedChanges: { variantList: unsavedVariant, settings: unsavedSettings },
    formActions: { asyncDispatchSaveDirtyVariantCard, asyncDispatchSaveDirtyExperimentSettings },
  } = useTakeoverExperimentContext();
  const { showModal, hideModal } = useModalContext();

  const handleOnClose = () => {
    /**
     * Callback set by the consumer of the experiment takeover.
     * This normally involves refetching data on the base page.
     *
     * Users should be able to close if there are errors
     */
    onClose();
    closeExperimentTakeover();
  };

  const unsavedExperimentModal = () =>
    showModal({
      variant: MODAL_VARIANTS.WARNING,
      id: 'unsaved-variants-modal',
      title: t('variantCard.unsavedModal.header'),
      body: t('variantCard.unsavedModal.body'),
      buttonText: t('variantCard.unsavedModal.button'),
      isDestructive: false,
      secondaryButton: {
        buttonText: t('variantCard.unsavedModal.secondaryButton'),
        onConfirm: () => {
          hideModal();
          handleOnClose();
        },
      },
      onConfirm: async () => {
        /**
         * Save any unsaved variant cards and settings
         * Then close the modal and takeover.
         * In case of an error just close the modal
         */
        try {
          if (unsavedVariant) {
            await asyncDispatchSaveDirtyVariantCard();
          }
          if (unsavedSettings) {
            await asyncDispatchSaveDirtyExperimentSettings();
          }
          hideModal();
          handleOnClose();
        } catch (e) {
          hideModal();
        }
      },
    });

  return (
    <PageContainer>
      {isOverlayEnable && <Overlay data-test={dataTestId.overlay} />}
      <Takeover
        isOverlay
        isMainApp
        noPadding
        withPortal
        onClose={unsavedVariant || unsavedSettings ? unsavedExperimentModal : handleOnClose}
        HeaderBarContent={<ExperimentTakeoverHeader />}
        Body={
          <ErrorBoundary teamName={Team.EXPERIMENT_TEAM} productName={Product.EXPERIMENT_EDITOR}>
            <ExperimentTakeoverBody handleOnClose={handleOnClose} />
          </ErrorBoundary>
        }
        Footer={<ExperimentTakeoverFooter onClose={onClose} />}
      />
    </PageContainer>
  );
};

export const ExperimentEditDrawer = ({ onClose }: ExperimentEditDrawerProps) => {
  const loading = useSelector(getTakeoverExperimentLoading);
  const campaignId = useSelector(getTakeoverCampaignId);
  const variantList = useSelector(getTakeoverVariantList);

  return loading || !campaignId || !variantList?.length ? (
    <Spinner />
  ) : (
    <ExperimentEditDrawerContext>
      <ExperimentTakeOver onClose={onClose} />
    </ExperimentEditDrawerContext>
  );
};
